/**
 * Suite wallet via ProxyPro JWT — same balance as VividLex / BookForge / ImageForge / VoiceForge.
 * Debit client: visionforge
 */

import { api, getAccessToken, isLoggedIn } from "./auth";
import type { WalletSnapshot } from "./types";

const DEFAULT_PACKS = [5, 10, 20, 50, 100];
const CLIENT = "visionforge";

function packsFromData(data: Record<string, unknown>): { amountUsd: number; label: string }[] {
  const notes = (data.notes || {}) as Record<string, unknown>;
  const raw =
    data.credit_packs_usd ?? data.creditPacksUsd ?? notes.credit_packs_usd ?? null;
  if (Array.isArray(raw) && raw.length) {
    return raw.map((n) => {
      const usd = Number(n);
      return { amountUsd: usd, label: `$${usd}` };
    });
  }
  return DEFAULT_PACKS.map((usd) => ({ amountUsd: usd, label: `$${usd}` }));
}

function parseWalletUsd(data: Record<string, unknown>): number {
  const balances = (data.balances || {}) as Record<string, unknown>;
  for (const c of [
    data.wallet_usd,
    data.walletUsd,
    data.balanceUsd,
    data.balance_usd,
    balances.wallet_usd,
    balances.walletUsd,
    balances.credits_usd,
  ]) {
    if (c === null || c === undefined || c === "") continue;
    const n = Number(c);
    if (Number.isFinite(n)) return n;
  }
  for (const c of [
    data.wallet_balance_cents,
    data.walletBalanceCents,
    balances.wallet_balance_cents,
  ]) {
    if (c === null || c === undefined || c === "") continue;
    const n = Number(c);
    if (Number.isFinite(n)) return n / 100;
  }
  return 0;
}

function parseMember(data: Record<string, unknown>): boolean {
  if (data.subscription_active === true || data.member_active === true) return true;
  if (data.subscriptionActive === true || data.memberActive === true) return true;
  const member = data.member as { active?: boolean } | undefined;
  if (member?.active) return true;
  const tier = String(data.tier || "").toLowerCase();
  if (["member", "starter", "pro", "business", "spark", "creator", "studio"].includes(tier)) {
    return true;
  }
  return false;
}

export async function fetchWallet(): Promise<WalletSnapshot> {
  if (!isLoggedIn() || !getAccessToken()) {
    return {
      balanceUsd: 0,
      currency: "USD",
      packs: packsFromData({}),
    };
  }

  const errors: string[] = [];

  try {
    const caps = await api<Record<string, unknown>>(
      `/api/auth/capabilities?client=${encodeURIComponent(CLIENT)}`,
    );
    const balanceUsd = parseWalletUsd(caps);
    const member = parseMember(caps);
    return {
      balanceUsd,
      currency: "USD",
      subscriptionActive: member,
      subscriptionLabel: member ? "Member" : null,
      stripeConfigured: true,
      packs: packsFromData(caps),
      adultOk: Boolean(caps.adult_ok),
      catalogTabs:
        caps.catalog_tabs && typeof caps.catalog_tabs === "object"
          ? (caps.catalog_tabs as Record<string, boolean>)
          : {},
    };
  } catch (e) {
    errors.push(`capabilities: ${e instanceof Error ? e.message : String(e)}`);
  }

  try {
    const sub = await api<Record<string, unknown> | null>("/api/billing/subscription");
    if (sub && typeof sub === "object") {
      const balanceUsd = parseWalletUsd(sub);
      const member = parseMember(sub);
      return {
        balanceUsd,
        currency: "USD",
        subscriptionActive: member,
        subscriptionLabel: member ? String(sub.tier || "Member") : null,
        stripeConfigured: true,
        packs: packsFromData(sub),
        adultOk: Boolean(sub.adult_ok),
        catalogTabs:
          sub.catalog_tabs && typeof sub.catalog_tabs === "object"
            ? (sub.catalog_tabs as Record<string, boolean>)
            : {},
      };
    }
  } catch (e) {
    errors.push(`subscription: ${e instanceof Error ? e.message : String(e)}`);
  }

  const joined = errors.join(" · ") || "Wallet request failed";
  if (/session expired|401|unauthorized|sign in/i.test(joined)) {
    throw new Error("Session expired. Sign out and Continue with Google again.");
  }
  console.warn("[suite wallet]", joined);
  return {
    balanceUsd: 0,
    currency: "USD",
    packs: packsFromData({}),
  };
}

export async function fetchWalletHistory(opts?: {
  limit?: number;
  before_id?: number;
  suite?: string;
}): Promise<{
  wallet_balance_cents: number;
  items: {
    id: number;
    ts: string | null;
    kind: string;
    amount_cents: number;
    suite: string;
    product: string;
    reason: string;
  }[];
  next_before_id: number | null;
}> {
  if (!getAccessToken()) throw new Error("Sign in to view wallet history.");
  const q = new URLSearchParams();
  q.set("limit", String(opts?.limit ?? 50));
  if (opts?.before_id) q.set("before_id", String(opts.before_id));
  if (opts?.suite) q.set("suite", opts.suite);
  return api(`/api/billing/wallet-history?${q}`);
}

export async function startCheckout(amountUsd: number): Promise<string | null> {
  if (!getAccessToken()) throw new Error("Sign in to add suite credits.");
  const data = await api<Record<string, unknown>>("/api/billing/checkout", {
    method: "POST",
    body: JSON.stringify({
      tier: `credit_${Math.round(amountUsd)}`,
      amount_usd: amountUsd,
      client: CLIENT,
      success_url: `${window.location.origin}/?checkout=success`,
      cancel_url: `${window.location.origin}/?checkout=cancel`,
    }),
  });
  return (
    (data.checkout_url as string) ||
    (data.checkoutUrl as string) ||
    (data.url as string) ||
    null
  );
}

export async function subscribeMember(): Promise<string | null> {
  if (!getAccessToken()) throw new Error("Sign in to subscribe.");
  const data = await api<Record<string, unknown>>("/api/billing/checkout", {
    method: "POST",
    body: JSON.stringify({
      tier: "member",
      client: CLIENT,
      success_url: `${window.location.origin}/?checkout=success`,
      cancel_url: `${window.location.origin}/?checkout=cancel`,
    }),
  });
  return (
    (data.checkout_url as string) ||
    (data.checkoutUrl as string) ||
    (data.url as string) ||
    null
  );
}
